import { assetObjectUrl } from "../../features/studio-controller/StudioServices";
import {  PictureLayer, TextLayer } from "../../types/videoType";

export type TextStyleKey =
  | "classic"
  | "box"
  | "bold"
  | "serif"
  | "neon"
  | "type"
  | "caption"
  | "pill"
  | "marker"
  | "hindi";

export type LayerMode =
  | "shadow"
  | "box"
  | "outline"
  | "neon"
  | "plain";



export interface TextStyle {
  key: TextStyleKey;
  label: string;
  font: string;
  weight: number;
  color: string;
  mode: LayerMode;

  bg?: string;
  stroke?: string;
  glow?: string;
  italic?: boolean;

  /**
   * Position and size are fractions of the frame.
   */
  y?: number;
  size?: number;
}

export interface LayerAnimation {
  alpha: number;
  dx: number;
  dy: number;
}


export type VideoLayer =
  | TextLayer
  | PictureLayer;

export interface PictureCache {
  promise: Promise<HTMLImageElement>;
  img: HTMLImageElement | null;
}

export interface GalleryLayerItem {
  mediaUrl: string;
  templateName?: string | null;
}

export interface LayerBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/* -------------------------------------------------------------------------- */
/* Text styles                                                                */
/* -------------------------------------------------------------------------- */

export const TEXT_STYLES: readonly TextStyle[] = [
  {
    key: "classic",
    label: "Classic",
    font: "Poppins",
    weight: 700,
    color: "#ffffff",
    mode: "shadow",
  },

  {
    key: "box",
    label: "Box",
    font: "Poppins",
    weight: 600,
    color: "#10231e",
    bg: "#ffffff",
    mode: "box",
  },

  {
    key: "bold",
    label: "Bold",
    font: "Bebas Neue",
    weight: 400,
    color: "#ffd166",
    mode: "outline",
    stroke: "#10231e",
  },

  {
    key: "serif",
    label: "Elegant",
    font: "Playfair Display",
    weight: 700,
    color: "#ffffff",
    mode: "shadow",
    italic: true,
  },

  {
    key: "neon",
    label: "Neon",
    font: "Montserrat",
    weight: 700,
    color: "#ffffff",
    mode: "neon",
    glow: "#25d366",
  },

  {
    key: "type",
    label: "Typewriter",
    font: "Courier Prime",
    weight: 700,
    color: "#10231e",
    bg: "#ffd166",
    mode: "box",
  },

  {
    key: "caption",
    label: "Caption",
    font: "Poppins",
    weight: 600,
    color: "#ffffff",
    bg: "rgba(0,0,0,0.62)",
    mode: "box",
    y: 0.82,
    size: 0.038,
  },

  {
    key: "pill",
    label: "WhatsApp",
    font: "Poppins",
    weight: 700,
    color: "#062b1c",
    bg: "#25d366",
    mode: "box",
  },

  {
    key: "marker",
    label: "Marker",
    font: "Permanent Marker",
    weight: 400,
    color: "#ffd166",
    mode: "shadow",
  },

  {
    key: "hindi",
    label: "हिंदी",
    font: "Noto Sans Devanagari",
    weight: 700,
    color: "#ffffff",
    mode: "shadow",
  },
];

/* -------------------------------------------------------------------------- */
/* Picture cache                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Pictures used as layers (logos, product photos), loaded once
 * through the same-origin proxy.
 */
const pictures = new Map<string, PictureCache>();

export function layerPicture(
  assetUrl: string,
): PictureCache {
  const existing = pictures.get(assetUrl);

  if (existing) {
    return existing;
  }

  const promise = assetObjectUrl(assetUrl).then(
    (u: string) =>
      new Promise<HTMLImageElement>(
        (resolve, reject) => {
          const img = new Image();

          img.onload = () => resolve(img);
          img.onerror = () =>
            reject(
              new Error(
                `Failed to load image: ${assetUrl}`,
              ),
            );

          img.src = u;
        },
      ),
  );

  const entry: PictureCache = {
    promise,
    img: null,
  };

  pictures.set(assetUrl, entry);

  promise
    .then((img) => {
      const cached = pictures.get(assetUrl);

      if (cached) {
        cached.img = img;
      }
    })
    .catch(() => {
      // Keep the cached promise so repeated requests don't
      // continuously create new image requests.
    });

  return entry;
}

/* -------------------------------------------------------------------------- */
/* Create picture layer                                                       */
/* -------------------------------------------------------------------------- */

export function newPictureLayer(
  item: GalleryLayerItem,
  clipLength: number,
): PictureLayer {
  return {
    id: `p${Date.now().toString(36)}${n++}`,

    kind: "image",

    assetUrl: item.mediaUrl,

    text:
      item.templateName ||
      "Picture",

    x: 0.82,
    y: 0.12,

    w: 0.22,

    radius: 0,

    start: 0,
    end: clipLength,

    animIn: "FADE",
    animOut: "NONE",
  };
}

/* -------------------------------------------------------------------------- */
/* Create text layer                                                          */
/* -------------------------------------------------------------------------- */

let n = 0;

export function newLayer(
  styleKey: TextStyleKey,
  clipLength: number,
  text = "Tap to edit",
): TextLayer {
  const s =
    TEXT_STYLES.find(
      (x) => x.key === styleKey,
    ) ?? TEXT_STYLES[0];

  return {
    id: `l${Date.now().toString(36)}${n++}`,

    kind: "text",

    text,

    x: 0.5,

    y: s.y ?? 0.45,

    size: s.size ?? 0.05,

    align: "center",

    animIn: "FADE",
    animOut: "FADE",

    font: s.font,

    weight: s.weight,

    italic: !!s.italic,

    color: s.color,

    bg: s.bg ?? "#ffffff",

    stroke: s.stroke ?? "#000000",

    glow: s.glow ?? "#25d366",

    mode: s.mode,

    start: 0,

    end: Math.min(
      clipLength,
      Math.max(3, clipLength),
    ),
  };
}

/* -------------------------------------------------------------------------- */
/* Create sticker                                                             */
/* -------------------------------------------------------------------------- */

export function newSticker(
  emoji: string,
  clipLength: number,
): TextLayer {
  return {
    ...newLayer(
      "classic",
      clipLength,
      emoji,
    ),

    size: 0.1,

    mode: "plain",

    sticker: true,

    y: 0.3,

    animIn: "POP",

    animOut: "NONE",
  };
}

/* -------------------------------------------------------------------------- */
/* Draw layer                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Draws one layer onto a W × H context.
 *
 * Returns its box in pixels for hit-testing.
 *
 * anim = { alpha, dx, dy }
 * preview of the in/out animation.
 */
export function drawLayer(
  ctx: CanvasRenderingContext2D,
  L: VideoLayer,
  W: number,
  H: number,
  anim?: LayerAnimation,
): LayerBox {
  if (L.kind === "image") {
    return drawPicture(
      ctx,
      L,
      W,
      H,
      anim,
    );
  }

  const size = Math.max(
    6,
    L.size * H,
  );

  ctx.save();

  if (anim) {
    ctx.globalAlpha *= anim.alpha;

    ctx.translate(
      anim.dx * W,
      anim.dy * H,
    );
  }

  ctx.font =
    `${L.italic ? "italic " : ""}` +
    `${L.weight} ${size}px ` +
    `"${L.font}", ` +
    `"Noto Sans Devanagari", ` +
    `sans-serif`;

  ctx.textBaseline = "middle";

  const lines = wrap(
    ctx,
    L.text || " ",
    W * 0.86,
  );

  const lineH = size * 1.22;

  const widths = lines.map(
    (line) =>
      ctx.measureText(line).width,
  );

  const blockW = Math.max(
    ...widths,
    size * 0.5,
  );

  const blockH =
    lines.length * lineH;

  const pad =
    L.mode === "box"
      ? size * 0.38
      : 0;

  const cx = L.x * W;
  const cy = L.y * H;

  const left =
    cx - blockW / 2;

  const top =
    cy - blockH / 2;

  /* ------------------------------ Box mode ------------------------------ */

  if (L.mode === "box") {
    ctx.fillStyle = L.bg;

    // One rounded box per line, like Instagram.
    lines.forEach((line, i) => {
      const w = widths[i];

      const x =
        L.align === "left"
          ? left
          : L.align === "right"
            ? left + blockW - w
            : cx - w / 2;

      roundRect(
        ctx,
        x - pad,
        top +
          i * lineH -
          pad * 0.35,
        w + pad * 2,
        lineH + pad * 0.7,
        size * 0.3,
      );

      ctx.fill();
    });
  }

  /* ------------------------------ Text ---------------------------------- */

  lines.forEach((line, i) => {
    const w = widths[i];

    const x =
      L.align === "left"
        ? left
        : L.align === "right"
          ? left + blockW - w
          : cx - w / 2;

    const y =
      top +
      i * lineH +
      lineH / 2;

    /* ----------------------------- Shadow ----------------------------- */

    if (L.mode === "shadow") {
      ctx.shadowColor =
        "rgba(0,0,0,0.55)";

      ctx.shadowBlur =
        size * 0.25;

      ctx.shadowOffsetY =
        size * 0.05;
    }

    /* ------------------------------ Neon ------------------------------ */

    if (L.mode === "neon") {
      ctx.shadowColor = L.glow;

      ctx.shadowBlur =
        size * 0.6;

      ctx.fillStyle = L.color;

      ctx.fillText(
        line,
        x,
        y,
      );
    }

    /* ----------------------------- Outline ---------------------------- */

    if (L.mode === "outline") {
      ctx.lineJoin = "round";

      ctx.lineWidth =
        size * 0.14;

      ctx.strokeStyle =
        L.stroke;

      ctx.strokeText(
        line,
        x,
        y,
      );
    }

    /* ------------------------------ Fill ------------------------------- */

    ctx.fillStyle = L.color;

    ctx.fillText(
      line,
      x,
      y,
    );

    ctx.shadowColor =
      "transparent";
  });

  ctx.restore();

  return {
    x: left - pad,
    y: top - pad,
    w: blockW + pad * 2,
    h: blockH + pad * 2,
  };
}

/* -------------------------------------------------------------------------- */
/* Draw picture                                                               */
/* -------------------------------------------------------------------------- */

function drawPicture(
  ctx: CanvasRenderingContext2D,
  L: PictureLayer,
  W: number,
  H: number,
  anim?: LayerAnimation,
): LayerBox {
  const pic = layerPicture(
    L.assetUrl,
  );

  const w = L.w * W;

  const img = pic.img;

  const h =
    img && img.naturalWidth > 0
      ? w *
        (img.naturalHeight /
          img.naturalWidth)
      : w;

  const x =
    L.x * W - w / 2;

  const y =
    L.y * H - h / 2;

  if (img) {
    ctx.save();

    if (anim) {
      ctx.globalAlpha *= anim.alpha;

      ctx.translate(
        anim.dx * W,
        anim.dy * H,
      );
    }

    if (L.radius) {
      roundRect(
        ctx,
        x,
        y,
        w,
        h,
        (L.radius / 100) *
          Math.min(w, h) /
          2,
      );

      ctx.clip();
    }

    ctx.drawImage(
      img,
      x,
      y,
      w,
      h,
    );

    ctx.restore();
  }

  return {
    x,
    y,
    w,
    h,
  };
}

/* -------------------------------------------------------------------------- */
/* Text wrapping                                                              */
/* -------------------------------------------------------------------------- */

function wrap(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxW: number,
): string[] {
  const out: string[] = [];

  for (const para of String(text).split("\n")) {
    const words = para.split(" ");

    let line = "";

    for (const word of words) {
      const test = line
        ? `${line} ${word}`
        : word;

      if (
        ctx.measureText(test).width >
          maxW &&
        line
      ) {
        out.push(line);

        line = word;
      } else {
        line = test;
      }
    }

    out.push(line);
  }

  return out;
}

/* -------------------------------------------------------------------------- */
/* Rounded rectangle                                                          */
/* -------------------------------------------------------------------------- */

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const rr = Math.min(
    r,
    w / 2,
    h / 2,
  );

  ctx.beginPath();

  ctx.moveTo(
    x + rr,
    y,
  );

  ctx.arcTo(
    x + w,
    y,
    x + w,
    y + h,
    rr,
  );

  ctx.arcTo(
    x + w,
    y + h,
    x,
    y + h,
    rr,
  );

  ctx.arcTo(
    x,
    y + h,
    x,
    y,
    rr,
  );

  ctx.arcTo(
    x,
    y,
    x + w,
    y,
    rr,
  );

  ctx.closePath();
}

/* -------------------------------------------------------------------------- */
/* Layers → PNGs                                                              */
/* -------------------------------------------------------------------------- */

/**
 * One full-frame transparent PNG per layer,
 * at the output size.
 */
export async function layersToPngs(
  layers: VideoLayer[],
  W: number,
  H: number,
): Promise<Blob[]> {
  const out: Blob[] = [];

  for (const L of layers) {
    // eslint-disable-next-line no-await-in-loop
    if (L.kind === "image") {
      await layerPicture(
        L.assetUrl,
      ).promise;
    }

    const canvas =
      document.createElement(
        "canvas",
      );

    canvas.width = W;
    canvas.height = H;

    const ctx =
      canvas.getContext("2d");

    if (!ctx) {
      throw new Error(
        "Could not create 2D canvas context",
      );
    }

    drawLayer(
      ctx,
      L,
      W,
      H,
    );

    // eslint-disable-next-line no-await-in-loop
    const blob = await new Promise<Blob>(
      (resolve, reject) => {
        canvas.toBlob(
          (result) => {
            if (result) {
              resolve(result);
            } else {
              reject(
                new Error(
                  "Failed to create PNG blob",
                ),
              );
            }
          },
          "image/png",
        );
      },
    );

    out.push(blob);
  }

  return out;
}

/* -------------------------------------------------------------------------- */
/* Time formatting                                                            */
/* -------------------------------------------------------------------------- */

export function fmtTime(
  s: number,
): string {
  if (!Number.isFinite(s)) {
    return "0:00";
  }

  const m = Math.floor(s / 60);

  const sec = Math.floor(
    s % 60,
  );

  const t = Math.floor(
    (s % 1) * 10,
  );

  return `${m}:${String(sec).padStart(
    2,
    "0",
  )}.${t}`;
}